import React from "react";
import { Mail, MapPin, Clock4 } from "lucide-react";
import { DownloadButtons } from "./DownloadButtons";
import { useLanguage } from "../i18n/LanguageContext";
import { trackEvent } from "../lib/analytics";

export const Contact = () => {
  const { t } = useLanguage();
  return (
    <section id="yhteystiedot" data-testid="contact-section" className="relative py-24 md:py-32 border-t-2 border-[#1a1a1a] grain overflow-hidden">
      <div className="absolute top-8 left-5 md:left-10 font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3] uppercase">{t("contact.section_number")}</div>
      <div className="absolute -bottom-10 left-0 right-0 text-center pointer-events-none select-none font-display font-bold uppercase text-[#FFEA00]/[0.04] leading-none whitespace-nowrap" style={{ fontSize: "min(22vw, 320px)" }} aria-hidden="true">
        DUUNARI
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-4">{t("contact.eyebrow")}</div>
            <h2 data-testid="contact-heading" className="font-display font-bold text-white text-4xl md:text-6xl lg:text-7xl uppercase leading-none tracking-tight">
              {t("contact.heading_line1")}
              <br />
              <span className="text-[#FFEA00]">{t("contact.heading_line2_accent")}</span>.
              <br />
              {t("contact.heading_line3")}
            </h2>
            <p className="mt-6 max-w-xl text-[#A3A3A3] text-base md:text-lg">{t("contact.subline")}</p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span
                data-testid="contact-human-promise"
                className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#FFEA00] bg-[#FFEA00] text-black"
              >
                <span className="inline-block w-2 h-2 bg-black rounded-full pulse-dot" />
                <span className="font-display font-bold text-xs md:text-sm tracking-[0.15em] uppercase">
                  {t("contact.human_promise_short")}
                </span>
              </span>
              <span
                data-testid="contact-no-bot"
                className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#FFEA00]/60 bg-[#FFEA00]/[0.05]"
              >
                <span className="font-display font-bold text-xs md:text-sm tracking-[0.15em] uppercase text-white">
                  {t("contact.no_bot_promise")}
                </span>
              </span>
            </div>

            <div className="mt-10">
              <DownloadButtons variant="footer" testIdPrefix="contact" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border-2 border-[#FFEA00] bg-black p-7 relative">
              <div className="absolute -top-px -left-px w-3 h-3 bg-[#FFEA00]" />
              <div className="absolute -bottom-px -right-px w-3 h-3 bg-[#FFEA00]" />
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FFEA00] uppercase mb-6">{t("contact.card_title")}</div>

              <ContactRow icon={Mail} label={t("contact.email_label")} value="duunari.online@gmail.com" href="mailto:duunari.online@gmail.com" testId="contact-email" onClick={() => trackEvent("Click: Email")} />
              <div className="my-5 h-px bg-[#1f1f1f]" />
              <ContactRow icon={MapPin} label={t("contact.location_label")} value={t("contact.location_value")} testId="contact-location" />
              <div className="my-5 h-px bg-[#1f1f1f]" />
              <ContactRow icon={Clock4} label={t("contact.hours_label")} value={t("contact.hours_value")} testId="contact-hours" />

              <div className="mt-7 h-2 hazard-stripes-anim" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, testId, onClick }) => {
  const content = (
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 flex items-center justify-center border-2 border-[#1f1f1f] bg-[#0a0a0a] shrink-0">
        <Icon className="w-5 h-5 text-[#FFEA00]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3] uppercase">{label}</div>
        <div className="font-display text-white text-base md:text-lg uppercase tracking-wide truncate">{value}</div>
      </div>
    </div>
  );
  if (href) return <a href={href} data-testid={testId} onClick={onClick} className="block hover:text-[#FFEA00] group">{content}</a>;
  return <div data-testid={testId}>{content}</div>;
};
